// src/api/auth.js
import API from "../axiosConfig";

export const searchInfractionByFilter = async (filters) => {
  try {
    // Construct query parameters based on the provided filters
    const params = {};
    if (filters.matricule) params.matricule = filters.matricule;
    if (filters.reference) params.reference = filters.reference;

    if (filters.type_infraction_id)
      params.type_infraction_id = filters.type_infraction_id;
    if (filters.status) params.status = filters.status;
    if (filters.start_date && filters.end_date) {
      params.start_date = filters.start_date;
      params.end_date = filters.end_date;
    }
    if (filters.id_agent) params.id_agent = filters.id_agent;
    if (filters.zone_id) params.zone_id = filters.zone_id;
    if (filters.section_id) params.section_id = filters.section_id;
    if (filters.street_id) params.street_id = filters.street_id;
    if (filters.line_id) params.line_id = filters.line_id;

    // Make a GET request with the filters as query parameters
    const response = await API.get("/infractions/search", { params });
    return response.data; // Adjust based on your API response structure
  } catch (error) {
    throw error; // Rethrow error to be handled in the component
  }
};

export const searchInfractionsForPrint = async (matricule, idAgent) => {
  const response = await API.get("/infractions/search", {
    params: { matricule, print_search: 1, ...(idAgent ? { id_agent: idAgent } : {}) },
  });
  return response.data;
};

export const getInfraction = async (id, forPrint = false) => {
  try {
    const response = await API.get(`/infractions/${id}`, {
      params: forPrint ? { print_detail: 1 } : undefined,
    });
    return response.data; // Adjust based on your API response structure
  } catch (error) {
    throw error; // Rethrow error to be handled in the component
  }
};

export const cancelInfraction = async (id, payload) => {
  try {
    const response = await API.put(`/infractions/cancel/${id}`, { ...payload });
    return response.data; // Adjust based on your API response structure
  } catch (error) {
    throw error; // Rethrow error to be handled in the component
  }
};

export const blockInfraction = async (id) => {
  try {
    const response = await API.put(`/infractions/block/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const unblockInfraction = async (id) => {
  try {
    const response = await API.put(`/infractions/unblock/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkBlockInfractions = async (ids) => {
  try {
    const response = await API.post(`/infractions/bulk-block`, { ids });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ─── Data Correction & Override (critical, audited admin actions) ───

/**
 * Roll back a paid infraction to 'pending': soft-deletes its payment & transaction
 * and (optionally) deducts the collected amount from the collecting agent's balance.
 * @param {number|string} id - Infraction id.
 * @param {{reason:string, update_agent_solde:boolean}} data
 */
export const rollbackInfractionPayment = async (id, data) => {
  try {
    const response = await API.put(`/infractions/${id}/rollback-payment`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Aggregated critical-change history for an infraction (across infraction,
 * payments, transactions, agents and the linked warning).
 */
export const getInfractionCriticalChanges = async (id) => {
  try {
    const response = await API.get(`/infractions/${id}/critical-changes`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Reverse an infraction cancellation (canceled → pending).
 * @param {number|string} id - Infraction id.
 * @param {string} reason - Mandatory reason.
 */
export const reverseCancelInfraction = async (id, reason) => {
  try {
    const response = await API.put(`/infractions/${id}/reverse-cancel`, { reason });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Generate a missing (retroactive) warning linked to an infraction.
 * @param {number|string} id - Infraction id.
 * @param {string} reason - Mandatory audit reason.
 */
export const createRetroactiveWarning = async (id, reason) => {
  try {
    const response = await API.post(`/infractions/${id}/retroactive-warning`, { reason });
    return response.data;
  } catch (error) {
    throw error;
  }
};
